import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { customSafariService } from '../services/customSafariService';
import { tourService } from '../services/tourService';

interface NavigationCounts {
    wishlist: number;
    savedSafaris: number;
    myBookings: number;
}

interface NavigationContextType {
    counts: NavigationCounts;
    refreshCounts: () => Promise<void>;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, isAuthenticated } = useAuth();
    const [counts, setCounts] = useState<NavigationCounts>({
        wishlist: 0,
        savedSafaris: 0,
        myBookings: 0
    });

    const refreshCounts = useCallback(async () => {
        try {
            // 1. Wishlist Count (from localStorage or Service if available)
            const favoriteIds = JSON.parse(localStorage.getItem(`favorites_${user?.id || 'guest'}`) || '[]');
            const wishlistCount = favoriteIds.length;

            // 2. Saved Safaris Count (from customSafariService)
            const cartItems = await customSafariService.getCart(user?.id);
            const savedSafarisCount = cartItems.length;

            // 3. My Bookings Count (from localStorage - st_my_bookings)
            const bookingIds = JSON.parse(localStorage.getItem('st_my_bookings') || '[]');
            const myBookingsCount = bookingIds.length;

            setCounts({
                wishlist: wishlistCount,
                savedSafaris: savedSafarisCount,
                myBookings: myBookingsCount
            });
        } catch (error) {
            console.error('Error refreshing navigation counts:', error);
        }
    }, [user]);

    // Initial load and auth change
    useEffect(() => {
        refreshCounts();
    }, [refreshCounts]);

    // Sync across tabs & update on storage changes
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (
                e.key?.startsWith('favorites_') ||
                e.key === 'customSafariCart' ||
                e.key === 'st_my_bookings'
            ) {
                refreshCounts();
            }
        };

        window.addEventListener('storage', handleStorageChange);

        // Custom event for same-tab updates
        const handleUpdate = () => refreshCounts();
        window.addEventListener('navigationCountsUpdate', handleUpdate);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('navigationCountsUpdate', handleUpdate);
        };
    }, [refreshCounts]);

    return (
        <NavigationContext.Provider value={{ counts, refreshCounts }}>
            {children}
        </NavigationContext.Provider>
    );
};

export const useNavigation = () => {
    const context = useContext(NavigationContext);
    if (context === undefined) {
        throw new Error('useNavigation must be used within a NavigationProvider');
    }
    return context;
};

/**
 * Utility to trigger count refresh from anywhere
 */
export const triggerNavigationUpdate = () => {
    window.dispatchEvent(new Event('navigationCountsUpdate'));
};
